import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import ChangeStatusForm from "../components/members/changeStatusForm";
import CreateAdminForm from "../components/members/createAdminForm";
import { deleteMember, getAllMembers } from "../services/memberService";
import useConfirm from "../hooks/useConfirm";
import ConfirmModal from "../components/ui/ConfirmModal";
import Skeleton from "../components/ui/Skeleton";
import "../assets/css/pages/members.css";

const isToday = (dt) => dt && moment(dt).isSame(moment(), "day");
const fmtTime = (t) => (t ? moment(t, "HH:mm:ss").format("hh:mm a") : null);

// Last check-in is only meaningful with the day attached: a bare time on an
// "Absent" member reads as if they checked in today.
const fmtLastCheckIn = (m) => {
  const time = fmtTime(m.lastCheckIn);
  if (!time || !m.checkin) return "—";
  return `${isToday(m.checkin) ? "Today" : moment(m.checkin).format("MMM D")}, ${time}`;
};

// Members in these states aren't expected to check in, so they must NOT be tagged
// "Absent" alongside active members. They get their own tag and are rendered dimmed.
const MUTED_STATUS = { DEACTIVATED: "deactivated", INACTIVE: "inactive", HIDDEN: "hidden" };
const isMuted = (m) => !!MUTED_STATUS[m.status];

const STATUS_LABEL = { present: "Present", late: "Late", absent: "Absent", deactivated: "Deactivated", inactive: "Inactive", hidden: "Hidden" };

const Members = () => {
  const navigate = useNavigate();
  const { confirm, modalProps } = useConfirm();
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [searchText, setSearchText] = useState("");
  const [view, setView] = useState("grid"); // grid | list
  const [openMenu, setOpenMenu] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showCreateAdminForm, setShowCreateAdminForm] = useState(false);
  const [showChangeStatusForm, setShowChangeStatusForm] = useState(false);

  const loadMembers = async () => {
    setLoading(true);
    const members = (await getAllMembers()) ?? [];
    setAllMembers(members);
    setLoading(false);
  };
  useEffect(() => { loadMembers(); }, []);

  const statusOf = (m) => {
    if (MUTED_STATUS[m.status]) return MUTED_STATUS[m.status]; // deactivated / inactive / hidden
    if (!isToday(m.checkin)) return "absent";
    return m.isLate ? "late" : "present";
  };
  const typeOf = (m) => {
    const st = statusOf(m);
    if (st !== "present" && st !== "late") return "none";
    return m.workType === "REMOTE" ? "remote" : "office";
  };

  const counts = useMemo(() => ({
    admins: allMembers.filter((m) => m.isDashboardAdmin && !isMuted(m)).length,
    inactive: allMembers.filter((m) => m.status === "INACTIVE").length,
    hidden: allMembers.filter((m) => m.status === "HIDDEN").length,
    deactivated: allMembers.filter((m) => m.status === "DEACTIVATED").length,
  }), [allMembers]);

  const CHIPS = useMemo(() => {
    const base = ["All", "Present", "Late", "Absent", "Office", "Remote"];
    if (counts.admins) base.push("Admins");
    if (counts.inactive) base.push("Inactive");
    if (counts.hidden) base.push("Hidden");
    if (counts.deactivated) base.push("Deactivated");
    return base;
  }, [counts]);

  const members = useMemo(() => {
    const q = searchText.toLowerCase().trim();
    return allMembers.filter((m) => {
      const hay = `${m.memberName} ${m.memberDisplayName} ${m.memberTitle || ""}`.toLowerCase();
      if (q && !hay.includes(q)) return false;
      const st = statusOf(m);
      switch (filter) {
        // present/late/absent/office/remote can never be a muted status, so they
        // exclude inactive/hidden/deactivated automatically.
        case "Present": return st === "present";
        case "Late": return st === "late";
        case "Absent": return st === "absent";
        case "Office": return typeOf(m) === "office";
        case "Remote": return typeOf(m) === "remote";
        case "Admins": return !!m.isDashboardAdmin && !isMuted(m);
        case "Inactive": return st === "inactive";
        case "Hidden": return st === "hidden";
        case "Deactivated": return st === "deactivated";
        // "All" means all ACTIVE members — inactive/hidden/deactivated people have their
        // own chips, so listing them here too just pads the count with people who no
        // longer check in.
        default: return !isMuted(m);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMembers, searchText, filter]);

  const openCreateAdminForm = (m) => { setSelectedMember(m); setShowCreateAdminForm(true); setOpenMenu(null); };
  const openChangeStatusForm = (m) => { setSelectedMember(m); setShowChangeStatusForm(true); setOpenMenu(null); };
  const handleMemberDelete = async (m) => {
    setOpenMenu(null);
    const ok = await confirm({ title: `Delete ${m.memberName}?`, message: "This removes the member and all their attendance records." });
    if (!ok) return;
    const prev = allMembers;
    setAllMembers((list) => list.filter((x) => x.memberId !== m.memberId));
    toast.success(`${m.memberName} deleted`);
    const status = await deleteMember(m.memberId);
    if (status !== 200) { toast.error("Something went wrong!"); setAllMembers(prev); }
  };

  const Avatar = ({ m, size = 46 }) =>
    m.memberImgUrl ? (
      <img className={`av av-${size}`} src={m.memberImgUrl} alt="" style={{ objectFit: "cover" }} />
    ) : (
      <div className={`av av-${size} av-MH`}>{(m.memberName || "?").slice(0, 2).toUpperCase()}</div>
    );

  const DotsMenu = ({ m }) => (
    <div className="mc-menu" onClick={(e) => e.stopPropagation()}>
      <button className="mc-menu-item" onClick={() => navigate(`/members/${m.memberId}`)}>View profile</button>
      {!m.isDashboardAdmin && <button className="mc-menu-item" onClick={() => openCreateAdminForm(m)}>Make admin</button>}
      <button className="mc-menu-item" onClick={() => openChangeStatusForm(m)}>Change status</button>
      <button className="mc-menu-item danger" onClick={() => handleMemberDelete(m)}>Delete</button>
    </div>
  );

  const toggleMenu = (e, id) => { e.stopPropagation(); setOpenMenu(openMenu === id ? null : id); };

  return (
    <>
      {/* Toolbar */}
      <div className="members-toolbar">
        <div className="members-toolbar-option">
          <div className="members-search">
            <svg><use href="#i-search" /></svg>
            <input type="text" placeholder="Search by name, username or role…" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          </div>
          
            {CHIPS.map((c) => (
              <button key={c} className={`chip ${filter === c ? "on" : ""}`} onClick={() => setFilter(c)}>{c}</button>
            ))}
          
        </div>
        <div className="view-toggle">
          <button className={view === "grid" ? "on" : ""} onClick={() => setView("grid")} aria-label="Grid view"><svg><use href="#i-grid" /></svg></button>
          <button className={view === "list" ? "on" : ""} onClick={() => setView("list")} aria-label="List view"><svg><use href="#i-list" /></svg></button>
        </div>
      </div>

      {/* Count */}
      <div className="members-count">
        {loading ? "Loading members…" : <>Showing <b>{members.length}</b> member{members.length === 1 ? "" : "s"}</>}
      </div>

      {/* Grid view */}
      {view === "grid" && (
        <div className="members-grid">
          {loading && Array.from({ length: 9 }).map((_, i) => (
            <div className="member-card" key={`sk-${i}`} style={{ cursor: "default" }}>
              <div className="mcard-top">
                <span className="skeleton skeleton-circle" style={{ width: 46, height: 46 }} />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}><Skeleton width="60%" /><Skeleton width="40%" /></div>
              </div>
              <div className="mcard-foot"><Skeleton width={70} /><Skeleton width={54} /></div>
            </div>
          ))}

          {!loading && members.map((m) => {
            const st = statusOf(m);
            const type = typeOf(m);
            return (
              <div className={`member-card ${isMuted(m) ? "dimmed" : ""}`} key={m.memberId} onClick={() => navigate(`/members/${m.memberId}`)} role="button" tabIndex={0}>
                <div className="mcard-top">
                  <Avatar m={m} size={46} />
                  <div className="mcard-info">
                    <div className="mcard-name">{m.memberDisplayName || m.memberName}</div>
                    <div className="mcard-role">{m.memberTitle || m.memberName}</div>
                  </div>
                  <span className={`mstatus ${st}`}>{STATUS_LABEL[st]}</span>
                </div>
                <div className="mcard-foot">
                  <span className={`mtype ${type}`}>
                    {type !== "none" && <svg><use href={`#i-${type === "remote" ? "wifi" : "building"}`} /></svg>}
                    {type === "remote" ? "Remote" : type === "office" ? "Office" : "—"}
                  </span>
                  <div className="right">
                    <span className="mhours">{fmtLastCheckIn(m)}</span>
                    <button className="mcard-dots" onClick={(e) => toggleMenu(e, m.memberId)} aria-label="Member actions"><svg><use href="#i-dots" /></svg></button>
                  </div>
                  {openMenu === m.memberId && <DotsMenu m={m} />}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List view */}
      {view === "list" && !loading && (
        <div className="card members-list" style={{ overflowX: "auto" }}>
          <table className="tbl">
            <thead>
              <tr><th>Member</th><th>Title</th><th>Status</th><th>Type</th><th>Last check-in</th><th>Account</th><th></th></tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const st = statusOf(m);
                const type = typeOf(m);
                return (
                  <tr key={m.memberId} className={isMuted(m) ? "dimmed" : ""} onClick={() => navigate(`/members/${m.memberId}`)}>
                    <td><div className="mem-cell"><Avatar m={m} size={32} /><div className="mem-info"><b>{m.memberDisplayName || m.memberName}</b><span>{m.memberName}</span></div></div></td>
                    <td>{m.memberTitle || "—"}</td>
                    <td><span className={`mstatus ${st}`}>{STATUS_LABEL[st]}</span></td>
                    <td><span className={`mtype ${type}`}>{type === "remote" ? "Remote" : type === "office" ? "Office" : "—"}</span></td>
                    <td className="log-time">{fmtLastCheckIn(m)}</td>
                    <td>{m.isDashboardAdmin ? "Admin" : "Member"}</td>
                    <td>
                      <div className="acts" onClick={(e) => e.stopPropagation()} style={{ position: "relative" }}>
                        <button className="act" onClick={(e) => toggleMenu(e, m.memberId)} aria-label="Actions"><svg><use href="#i-dots" /></svg></button>
                        {openMenu === m.memberId && <DotsMenu m={m} />}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && members.length === 0 && (
        <div style={{ color: "var(--muted)", padding: 24, textAlign: "center" }}>No members found.</div>
      )}

      {/* click-away for the dots menu */}
      {/* {openMenu && <div onClick={() => setOpenMenu(null)} style={{ position: "fixed", inset: 0, zIndex: 15 }} />} */}

      {showCreateAdminForm && <CreateAdminForm member={selectedMember} onClose={() => setShowCreateAdminForm(false)} />}
      {showChangeStatusForm && <ChangeStatusForm member={selectedMember} onClose={() => setShowChangeStatusForm(false)} />}
      <ConfirmModal {...modalProps} />
    </>
  );
};

export default Members;
